import { useEffect } from "react";
import { create } from "zustand";
import { onGlobalKey, setGlobalBinds } from "@shared/lib/desktop";
import { useAudioTuning } from "./audio";

/**
 * Рация — «Режим рации» Discord: микрофон открыт, пока держат клавишу.
 *
 * Микрофон не выключается, а глушится в тракте (`@shared/denoiser` смотрит
 * сюда): выключенный LiveKit показал бы соседям зачёркнутый микрофон, а в
 * Discord у говорящего по рации его нет — он просто молчит.
 *
 * Клавишу слышит окно Airel, а в оболочке — и поверх игр (`electron/keys.ts`).
 * В вебе, как и у веб-версии Discord, — только вкладка.
 */
const usePush = create<{ open: boolean }>(() => ({ open: true }));

export function pushOpen(): boolean {
  return usePush.getState().open;
}

export function onPushChange(listener: (open: boolean) => void): () => void {
  return usePush.subscribe((state) => listener(state.open));
}

function setPushOpen(open: boolean): void {
  if (usePush.getState().open !== open) usePush.setState({ open });
}

/** Открыт ли микрофон рацией прямо сейчас — для отметки в настройках. */
export function usePushOpen(): boolean {
  return usePush((state) => state.open);
}

/**
 * Код кнопки мыши в том же виде, что у клавиш: средняя — «Mouse3», боковые —
 * «Mouse4» и «Mouse5». Левая и правая заняты интерфейсом и рацией не бывают.
 */
const MOUSE: Record<number, string> = { 1: "Mouse3", 3: "Mouse4", 4: "Mouse5" };

export function mouseCode(button: number): string | null {
  return MOUSE[button] ?? null;
}

const NAMES: Record<string, string> = {
  Space: "Пробел",
  Backquote: "`",
  CapsLock: "Caps Lock",
  Tab: "Tab",
  ControlLeft: "Левый Ctrl",
  ControlRight: "Правый Ctrl",
  ShiftLeft: "Левый Shift",
  ShiftRight: "Правый Shift",
  AltLeft: "Левый Alt",
  AltRight: "Правый Alt",
  Mouse3: "Средняя кнопка мыши",
  Mouse4: "Кнопка мыши 4",
  Mouse5: "Кнопка мыши 5",
};

/** Как назвать клавишу человеку: «V», «F5», «Левый Ctrl», «Кнопка мыши 4». */
export function keyName(code: string): string {
  if (NAMES[code]) return NAMES[code];
  const letter = /^Key([A-Z])$/.exec(code);
  if (letter) return letter[1];
  const digit = /^(?:Digit|Numpad)(\d)$/.exec(code);
  if (digit) return digit[1];
  return code;
}

/**
 * Слушает клавишу рации, пока идёт звонок и выбран режим «по кнопке». В
 * остальное время микрофон открыт — им распоряжается затвор «по голосу».
 *
 * Отпущенную клавишу микрофон держит ещё `pushDelay` мс, как «задержка
 * отпускания» в Discord: иначе обрезался бы хвост последнего слова.
 *
 * Нажатие слышат двое: окно и оболочка. Оба говорят «нажато» и «отпущено»
 * одной клавиши, повтор ничего не меняет. Окно потеряло фокус — микрофон
 * закрывается сразу, если оболочка не держит клавишу сама: иначе отпускания
 * уже не услышать.
 */
export function usePushToTalk(live: boolean): void {
  const [tuning] = useAudioTuning();
  // Без клавиши рации микрофон работает по голосу: закрыть его навсегда, пока
  // клавишу не задали, значило бы молчать в звонке, не зная почему.
  const push = live && tuning.mode === "push" && !!tuning.pushKey;
  const { pushKey, pushDelay } = tuning;

  useEffect(() => {
    if (!push || !pushKey) {
      setPushOpen(true);
      return;
    }

    setPushOpen(false);
    let release: ReturnType<typeof setTimeout> | undefined;
    const press = (code: string | null) => {
      if (!code || code !== pushKey) return;
      clearTimeout(release);
      setPushOpen(true);
    };
    const lift = (code: string | null) => {
      if (!code || code !== pushKey) return;
      clearTimeout(release);
      release = setTimeout(() => setPushOpen(false), pushDelay);
    };
    const keydown = (event: KeyboardEvent) => {
      if (!event.repeat) press(event.code);
    };
    const keyup = (event: KeyboardEvent) => lift(event.code);
    // Боковые кнопки мыши листают историю, а приложение ходит по адресу: без
    // `preventDefault` нажатие рации переносило бы в прошлый канал.
    const mousedown = (event: MouseEvent) => {
      const code = mouseCode(event.button);
      if (code === pushKey) event.preventDefault();
      press(code);
    };
    const mouseup = (event: MouseEvent) => {
      const code = mouseCode(event.button);
      if (code === pushKey) event.preventDefault();
      lift(code);
    };
    /** Оболочка держит клавишу рации — ей и отпускать. */
    let heldOutside = false;
    const blur = () => {
      if (heldOutside) return;
      clearTimeout(release);
      setPushOpen(false);
    };
    setGlobalBinds("push", [{ action: "ptt", keys: [pushKey] }]);
    const offGlobal = onGlobalKey((action, down) => {
      if (action !== "ptt") return;
      heldOutside = down;
      if (down) press(pushKey);
      else lift(pushKey);
    });

    window.addEventListener("keydown", keydown, true);
    window.addEventListener("keyup", keyup, true);
    window.addEventListener("mousedown", mousedown, true);
    window.addEventListener("mouseup", mouseup, true);
    window.addEventListener("blur", blur);
    return () => {
      clearTimeout(release);
      setGlobalBinds("push", []);
      offGlobal();
      window.removeEventListener("keydown", keydown, true);
      window.removeEventListener("keyup", keyup, true);
      window.removeEventListener("mousedown", mousedown, true);
      window.removeEventListener("mouseup", mouseup, true);
      window.removeEventListener("blur", blur);
      setPushOpen(true);
    };
  }, [push, pushKey, pushDelay]);
}
